import React from 'react'
import * as yup from 'yup'
import { Autocomplete, Box, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { Public, SchoolOutlined } from '@mui/icons-material'
import { useTranslation } from 'next-i18next'
import { useFormik } from 'formik'
import FlatTextField from '@components/FlatTextField'
import FlatButton from '@components/FlatButton'
import { useSchools } from '@api/school'
import { useRouter } from 'next/router'
import { debounce } from 'lodash'
import CreateAccountLayout from './CreateAccountLayout'
import countries from './countries'

const validationSchema = yup.object().shape({
  country: yup.mixed().required(),
  selected_school: yup.mixed().required(),
})

function ParentLogin() {
  const { t } = useTranslation()
  const { locale } = useRouter()
  // const countryCodeQuery = useGetCountryCode()
  // const defaultCountry = countryCodeQuery?.data ?? ''

  // const registerMutation = useRegister()
  const formik = useFormik({
    initialValues: {
      country: null,
      school_name: '',
      selected_school: null,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      window.location.href = `${values.selected_school.main_url}/${locale}`
      actions.setSubmitting(false)
    },
  })

  const [search, setSearch] = React.useState('')
  const handleSearchChange = React.useCallback(
    debounce((value) => setSearch(value), 500),
    []
  )
  React.useEffect(() => {
    handleSearchChange(formik.values.school_name)
  }, [formik.values.school_name])

  const schoolsQuery = useSchools(formik.values.country?.en, search)
  // const schoolsQuery = useSchools(formik.values.country?.code, search)

  return (
    <Box>
      <CreateAccountLayout
        title={t('parent-login:Parents_Log_In')}
        subtitle={t('parent-login:Log_in_with_your_school_name')}
      >
        <Box mt={6}>
          <Grid
            container
            spacing={5}
            component="form"
            onSubmit={formik.handleSubmit}
          >
            <Grid
              item
              xs={12}
            >
              <Autocomplete
                fullWidth
                id="country"
                name="country"
                autoComplete
                onChange={(_, value) => formik.setFieldValue('country', value)}
                value={formik.values.country}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Box>
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${option.id.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${option.id.toLowerCase()}.png 2x`}
                          alt=""
                        />
                      </Box>
                      <Typography>{option?.[locale] ?? option.en}</Typography>
                    </Stack>
                  </li>
                )}
                getOptionLabel={(option) => option?.[locale] ?? option.en}
                options={countries}
                noOptionsText={t('parent-login:noOptions')}
                renderInput={(params) => (
                  <FlatTextField
                    {...params}
                    onBlur={formik.handleBlur}
                    name="country"
                    label={t('parent-login:Select_Country')}
                    backgroundColor="neutral.main"
                    size="large"
                    error={formik.touched.country && Boolean(formik.errors.country)}
                    helperText={formik.touched.country && formik.errors.country}
                    StartIcon={Public}
                    inputProps={{
                      'aria-label': t('parent-login:Select_Country'),
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Autocomplete
                fullWidth
                id="selected-school"
                name="selected_school"
                disabled={!formik.values.country}
                autoComplete
                onInputChange={(_, newInputValue) =>
                  formik.setFieldValue('school_name', newInputValue)
                }
                onChange={(_, value) => formik.setFieldValue('selected_school', value)}
                value={formik.values.selected_school}
                inputValue={formik.values.school_name}
                filterOptions={(x) => x}
                getOptionLabel={(option) => option.name}
                options={schoolsQuery.data?.data ?? []}
                loading={schoolsQuery.isFetching}
                noOptionsText={
                  search.length < 3
                    ? t('parent-login:Enter_at_least_3_characters_to_show_results')
                    : t('parent-login:noOptions')
                }
                loadingText={`${t('parent-login:loading')}...`}
                renderInput={(params) => (
                  <FlatTextField
                    {...params}
                    onBlur={formik.handleBlur}
                    name="selected_school"
                    label={t('parent-login:Enter_School_Name')}
                    placeholder={t('parent-login:Enter_at_least_3_characters_to_show_results')}
                    backgroundColor="neutral.main"
                    size="large"
                    error={formik.touched.selected_school && Boolean(formik.errors.selected_school)}
                    helperText={formik.touched.selected_school && formik.errors.selected_school}
                    StartIcon={SchoolOutlined}
                    inputProps={{
                      'aria-label': t('parent-login:School_Name'),
                      ...params.inputProps,
                    }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {schoolsQuery.isFetching ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FlatButton
                variant="contained"
                sx={{ mb: 2 }}
                onClick={formik.handleSubmit}
                aria-label={t('parent-login:visit_school_Website')}
                type="submit"
              >
                {t('parent-login:visit_school_Website')}
              </FlatButton>
            </Grid>
          </Grid>
        </Box>
      </CreateAccountLayout>
    </Box>
  )
}

export default ParentLogin
